import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FreightForwarderBidService, FreightForwarderBidDto } from '../../../services/api/freight-forwarder-bid';
import { LanguageService } from '../../../services/i18n/language';
import { Subscription } from 'rxjs';
import { DocNumberPipe } from '../../../services/request-number';
import { competitionGain, CompetitionGain } from '../../../services/bid-savings';
import { HomeCurrencyService, fmtMoney } from '../../../services/home-currency';

/** Общий компонент секции ставок экспедиторов по запросу (для грузовладельца и таможенного брокера). */
@Component({
  selector: 'app-request-bids-section',
  standalone: true,
  imports: [CommonModule, RouterModule, DocNumberPipe],
  templateUrl: './request-bids-section.component.html',
  styleUrl: './request-bids-section.component.scss'
})
export class RequestBidsSectionComponent implements OnInit, OnDestroy {
  @Input() cargoRequestId!: number;
  /** Префикс маршрута для просмотра ставки: 'cargo-owner' или 'customs-broker'. */
  @Input() bidViewRoutePrefix: 'cargo-owner' | 'customs-broker' = 'cargo-owner';

  /** Событие после загрузки ставок (передаётся количество). Нужно грузовладельцу для hasBids. */
  @Output() bidsLoaded = new EventEmitter<number>();

  bids: FreightForwarderBidDto[] = [];
  loadingBids = false;
  errorBids: string | null = null;

  bidsSectionTitleText = 'Ставки экспедиторов';
  loadingBidsText = 'Загрузка ставок...';
  noBidsText = 'Ставок пока нет';
  bidNumberText = 'Номер ставки';
  forwarderText = 'Экспедитор';
  priceText = 'Стоимость перевозки';
  priceKztText = 'Стоимость перевозки в тенге';
  deliveryDaysText = 'Срок доставки';
  daysText = 'дней';
  statusText = 'Статус';
  pendingText = 'Ожидает';
  acceptedText = 'Принята';
  lostText = 'Проиграла';
  bestBidText = 'Выгоднее';

  /** Валюта компании — в ней считаем выгоду от конкурса. */
  private currency = 'KZT';

  private subscriptions = new Subscription();

  constructor(
    private bidService: FreightForwarderBidService,
    private homeCurrencyService: HomeCurrencyService,
    public router: Router,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.cargoRequestId != null) {
      this.loadBids();
    }
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => this.updateTranslations())
    );
    this.subscriptions.add(this.homeCurrencyService.currency$.subscribe(c => {
      this.currency = c;
      this.cdr.markForCheck();
    }));
  }

  /** Грузовладелец выбрал ставку дешевле средней по остальным — показываем, сколько выиграл на конкурсе. */
  get gain(): CompetitionGain | null {
    if (this.bidViewRoutePrefix !== 'cargo-owner') return null;
    const accepted = this.bids.find(b => b.bidStatus === 'ACCEPTED' || b.isAccepted);
    const g = accepted ? competitionGain(this.bids, accepted.id, this.currency) : null;
    return g && g.gain > 0 ? g : null;
  }

  gainMoney(v: number): string {
    return fmtMoney(v, this.currency, this.languageService.getCurrentLanguageValue());
  }

  t(key: string, fallback: string): string {
    return this.languageService.translate(key) || fallback;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    const prefix = 'cargoOwner.viewRequest';
    this.bidsSectionTitleText = this.languageService.translate(`${prefix}.bidsSectionTitle`) || 'Ставки экспедиторов';
    this.loadingBidsText = this.languageService.translate(`${prefix}.loadingBids`) || 'Загрузка ставок...';
    this.noBidsText = this.languageService.translate(`${prefix}.noBids`) || 'Ставок пока нет';
    this.bidNumberText = this.languageService.translate(`${prefix}.bidNumber`) || 'Номер ставки';
    this.forwarderText = this.languageService.translate(`${prefix}.forwarder`) || 'Экспедитор';
    this.priceText = this.languageService.translate(`${prefix}.price`) || 'Стоимость перевозки';
    this.priceKztText = this.languageService.translate(`${prefix}.priceKzt`) || 'Стоимость перевозки в тенге';
    this.deliveryDaysText = this.languageService.translate(`${prefix}.deliveryDays`) || 'Срок доставки';
    this.statusText = this.languageService.translate(`${prefix}.status`) || 'Статус';
    this.pendingText = this.languageService.translate(`${prefix}.pending`) || 'Ожидает';
    this.acceptedText = this.languageService.translate(`${prefix}.accepted`) || 'Принята';
    this.lostText = this.languageService.translate(`${prefix}.lost`) || 'Проиграла';
    this.bestBidText = this.languageService.translate(`${prefix}.bestBid`) || 'Выгоднее';
    this.cdr.markForCheck();
  }

  private loadBids(): void {
    this.loadingBids = true;
    this.errorBids = null;
    this.subscriptions.add(
      this.bidService.getBidsByCargoRequestId(this.cargoRequestId).subscribe({
        next: (bids) => {
          this.bids = bids;
          this.loadingBids = false;
          this.bidsLoaded.emit(bids.length);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('RequestBidsSection: Error loading bids', err);
          this.errorBids = 'Ошибка при загрузке ставок';
          this.loadingBids = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  getBidStatusText(bid: FreightForwarderBidDto): string {
    if (bid.bidStatus === 'ACCEPTED') return this.acceptedText;
    if (bid.bidStatus === 'LOST') return this.lostText;
    return this.pendingText;
  }

  getBidStatusClass(bid: FreightForwarderBidDto): string {
    if (bid.bidStatus === 'ACCEPTED') return 'accepted';
    if (bid.bidStatus === 'LOST') return 'lost';
    return 'pending';
  }

  viewBid(bidId: number): void {
    this.router.navigate([`/${this.bidViewRoutePrefix}/bid`, bidId]);
  }
}
